import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { MenuIcon, SearchIcon } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { PRIMARY_NAV } from '@/components/nav-items'
import { SidebarNav } from '@/components/sidebar-nav'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { returnToShelf } from '@/features/shelf/posture'
import { ThemeToggle } from '@/features/theme/ThemeToggle'
import { isActive } from '@/lib/nav'
import { cn } from '@/lib/utils'

/**
 * The desk shell (IA 1b): a sidebar in three groups and a top bar with search.
 * Below `lg` the sidebar gives way to a bottom bar with the four places a phone
 * needs most and a More sheet with the rest; there is no hamburger drawer.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background flex min-h-full">
      <aside className="border-divider hidden w-[216px] shrink-0 flex-col border-r lg:flex">
        <Wordmark className="px-[22px] pt-[18px] pb-4" />
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SidebarNav />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-background/90 border-divider sticky top-0 z-30 flex h-[57px] items-center gap-3 border-b px-4 backdrop-blur lg:px-6">
          <Wordmark className="lg:hidden" />
          <GlobalSearch />
          <span className="text-muted-foreground hidden text-xs whitespace-nowrap xl:inline">
            g p · g s · / to search
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/shelf"
              onClick={returnToShelf}
              className="text-steel-700 text-[13px] whitespace-nowrap lg:hidden"
            >
              Shelf
            </Link>
            <ThemeToggle />
          </div>
        </header>

        <main className="min-w-0 flex-1 pb-[76px] lg:pb-0">{children}</main>
      </div>

      <PhoneBar />
    </div>
  )
}

function Wordmark({ className }: { className?: string }) {
  return (
    <Link to="/" className={cn('font-heading text-xl font-semibold tracking-tight', className)}>
      Fundus
    </Link>
  )
}

/** The phone's bottom bar: the primary places plus More, which holds everything. */
function PhoneBar() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [moreOpen, setMoreOpen] = useState(false)
  const inPrimary = PRIMARY_NAV.some((item) => isActive(pathname, item.to))

  const tab =
    'font-heading flex flex-col items-center justify-center gap-1 text-[12px] font-semibold tracking-[0.04em] uppercase'

  return (
    <>
      <nav
        aria-label="Sections"
        className="bg-background border-divider fixed inset-x-0 bottom-0 z-30 grid h-[68px] grid-cols-5 border-t pb-[env(safe-area-inset-bottom)] lg:hidden"
      >
        {PRIMARY_NAV.map((item) => {
          const active = isActive(pathname, item.to)
          return (
            <Link
              key={item.to}
              to={item.to}
              activeOptions={{ exact: item.to === '/' }}
              aria-current={active ? 'page' : undefined}
              className={cn(
                tab,
                active ? 'text-steel-700 shadow-[inset_0_2px_0_var(--steel)]' : 'text-neutral-600',
              )}
            >
              <item.icon className="size-[22px]" strokeWidth={1.5} />
              {item.label}
            </Link>
          )
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-expanded={moreOpen}
          className={cn(
            tab,
            !inPrimary ? 'text-steel-700 shadow-[inset_0_2px_0_var(--steel)]' : 'text-neutral-600',
          )}
        >
          <MenuIcon className="size-[22px]" strokeWidth={1.5} />
          More
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="max-h-[85dvh] overflow-y-auto p-0">
          <SheetTitle className="px-5 pt-5">Everything</SheetTitle>
          <SidebarNav label="All sections" onNavigate={() => setMoreOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  )
}

function GlobalSearch() {
  const navigate = useNavigate()
  const [value, setValue] = useState('')

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    const q = value.trim()
    if (!q) return
    void navigate({ to: '/parts', search: { q } })
  }

  return (
    <form onSubmit={onSubmit} role="search" className="w-full max-w-[420px] min-w-0">
      <div className="relative">
        <SearchIcon
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          strokeWidth={1.5}
        />
        <Input
          type="search"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="Search parts…"
          aria-label="Search parts"
          className="pl-8 sm:pr-12"
        />
        <kbd
          aria-hidden="true"
          className="text-muted-foreground border-divider pointer-events-none absolute top-1/2 right-2 hidden -translate-y-1/2 border px-1 font-sans text-[11px] sm:block"
        >
          ⌘K
        </kbd>
      </div>
    </form>
  )
}
